/**
 * DashNavRail.jsx
 *
 * Shared bottom navigation rail component extracted from Home.jsx.
 * Provides a consistent frosted-glass bottom nav bar with FAB speed-dial
 * across all authenticated routes.
 *
 * Features:
 * - 5-tab grid: Home, Records, FAB (+), AI Assistant, Profile
 * - Active tab detection via current route (useLocation)
 * - FAB with speed-dial drop-up menu (Add Image, Book Appt)
 * - Frosted glass background with backdrop-filter blur
 * - Mask-image icons for filled/outlined tab states
 * - Accessible: aria-current for active tab, aria-label for FAB
 *
 * @component
 * @example
 * <DashNavRail />
 */
import {useState} from 'react';
import {Link, useNavigate, useLocation} from 'react-router';
import {UilCamera, UilCalendarAlt} from '@iconscout/react-unicons';
import './DashNavRail.css';

/**
 * Navigation tab configuration.
 * Each tab has a path, label, and icon definition.
 * Icons use mask-image PNGs for filled (active) and outlined (inactive) variants,
 * except Profile which uses an inline SVG for its custom avatar icon.
 */
const NAV_TABS = [
  {
    path: '/',
    label: 'Home',
    /** Filled icon for active state */
    filledIcon: '/assets/nav_ico_filled_1.png',
    /** Outlined icon for inactive state */
    outlinedIcon: '/assets/nav_ico_filled_1.png',
    type: 'mask',
  },
  {
    path: '/dental-records',
    label: 'Records',
    filledIcon: '/assets/nav_ico_outlined_4.png',
    outlinedIcon: '/assets/nav_ico_outlined_4.png',
    type: 'mask',
  },
  /* FAB placeholder — index 2 is the center FAB, not a real tab */
  null,
  {
    path: '/dental-ai',
    label: 'AI Assistant',
    filledIcon: '/assets/nav_ico_outlined_2.png',
    outlinedIcon: '/assets/nav_ico_outlined_2.png',
    type: 'mask',
  },
  {
    path: '/account',
    label: 'Profile',
    type: 'svg',
  },
];

/**
 * Speed-dial actions shown when FAB is open.
 * Each action has a route, label, icon, and stagger delay.
 */
const FAB_ACTIONS = [
  {
    path: '/oral-photo/add',
    label: 'Add Image',
    icon: <UilCamera size="18" color="#c43b71" />,
    delay: '0.1s',
  },
  {
    path: '/appointments/add',
    label: 'Book Appt',
    icon: <UilCalendarAlt size="18" color="#c43b71" />,
    delay: '0.05s',
  },
];

/**
 * Determines if a tab should be marked as active based on current pathname.
 * Home tab is active only on exact "/" match.
 * Other tabs are active if the pathname starts with their path.
 *
 * @param {string} tabPath - The tab's target path
 * @param {string} currentPath - Current browser pathname
 * @returns {boolean} Whether the tab is active
 */
function isTabActive(tabPath, currentPath) {
  if (tabPath === '/') {
    return currentPath === '/' || currentPath === '/dashboard';
  }
  return currentPath.startsWith(tabPath);
}

export default function DashNavRail() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isFabOpen, setIsFabOpen] = useState(false);

  return (
    <>
      {/* Semi-transparent overlay behind speed-dial menu */}
      <div
        className={`fab-overlay ${isFabOpen ? 'active' : ''}`}
        onClick={() => setIsFabOpen(false)}
        aria-hidden="true"
      />

      {/* Fixed bottom navigation rail container */}
      <div className="dash-nav-rail-parent">
        <div className="dash-nav-rail">
          {NAV_TABS.map((tab) => {
            /* Center position (index 2) renders the FAB instead of a tab */
            if (tab === null) {
              return (
                <div key="fab" className={`dash-fab-container ${isFabOpen ? 'active' : ''}`}>
                  {/* FAB button — rotates to X when open */}
                  <button
                    className={`dash-fab ${isFabOpen ? 'active' : ''}`}
                    onClick={() => setIsFabOpen(!isFabOpen)}
                    aria-label={isFabOpen ? 'Close quick actions' : 'Open quick actions'}
                    aria-expanded={isFabOpen}
                  >
                    <svg width={26} height={26} viewBox="0 0 24 24" fill="none">
                      <path d="M12 5v14M5 12h14" stroke="#fff" strokeWidth={2.4} strokeLinecap="round" />
                    </svg>
                  </button>

                  {/* Speed-dial drop-up menu with staggered animations */}
                  <div className="drop-up-menu">
                    {FAB_ACTIONS.map((action) => (
                      <Link
                        key={action.path}
                        to={action.path}
                        className="drop-up-item"
                        style={{transitionDelay: action.delay}}
                        onClick={() => setIsFabOpen(false)}
                      >
                        <div className="drop-up-item-icon">{action.icon}</div>
                        {action.label}
                      </Link>
                    ))}
                  </div>
                </div>
              );
            }

            const active = isTabActive(tab.path, location.pathname);

            return (
              <button
                key={tab.path}
                className="dash-nav-tab"
                aria-current={active ? 'page' : undefined}
                onClick={() => navigate(tab.path)}
              >
                <span className="dash-nav-tab-icon">
                  {tab.type === 'mask' ? (
                    /* Mask-image icon — color changes via background-color */
                    <span
                      className={`dash-nav-tab-mask ${active ? 'active' : ''}`}
                      style={{
                        WebkitMaskImage: `url(${active ? tab.filledIcon : tab.outlinedIcon})`,
                        maskImage: `url(${active ? tab.filledIcon : tab.outlinedIcon})`,
                      }}
                    />
                  ) : (
                    /* SVG icon for Profile tab */
                    <svg width={33} height={33} viewBox="0 0 24 24" fill="none">
                      <circle
                        cx={12}
                        cy={8}
                        r="3.6"
                        stroke={active ? '#C43B71' : '#9CA3AF'}
                        strokeWidth="1.7"
                      />
                      <path
                        d="M4.5 20a7.5 7.5 0 0115 0"
                        stroke={active ? '#C43B71' : '#9CA3AF'}
                        strokeWidth="1.7"
                        strokeLinecap="round"
                      />
                    </svg>
                  )}
                </span>
                <span className={`dash-nav-tab-label ${active ? 'active' : ''}`}>{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}
